import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin(CustomEase);

/**
 * Timings taken off the reference capture. The whole box animation runs in 300ms, with the expanded
 * content arriving afterwards rather than during, which is what keeps the open feeling immediate.
 */
const EXPAND_DURATION = 0.3;
const COLLAPSE_DURATION = 0.28;
/**
 * The white area's growth, taken off the reference frame by frame rather than picked by feel.
 *
 * Sampled every 33ms at 300ms total, the progression was 0.16, 0.37, 0.56, 0.72, 0.83, 0.91, 0.96,
 * 0.99, 1.00. Fitting a cubic bezier to those points gives this curve almost exactly.
 */
const ARTICLE_GROW_DURATION = 0.3;
const ARTICLE_GROW_EASE = CustomEase.create('articleGrow', '0.25, 0.30, 0.40, 1.00');
/**
 * Only ever present mid-flight. The card sits flat in the carousel, lifts while it travels, then
 * lands flat again as a fullscreen page that has nothing to cast a shadow onto.
 *
 * Measured off the reference rather than picked by eye. Sampling below the card's bottom edge, the
 * feed darkens by about 15/255 right at the edge and is back to its own colour by 18 to 21px out,
 * which is a far softer shadow than it looks like it should be.
 */
const LIFT_SHADOW = '0 4px 16px rgba(0, 0, 0, 0.10)';
/**
 * The expanded content stays on screen for most of the collapse and only clears at the very end.
 *
 * In the reference the article is still legible while the card is nearly back to carousel size,
 * which is what makes the close read as one continuous movement. Fading it first, then shrinking an
 * empty box, loses that entirely.
 */
const COLLAPSE_FADE = 0.12;

/**
 * A card that opens out to fullscreen when tapped.
 *
 * Two regions, both content agnostic. The header stays put and keeps whatever it holds at a steady
 * size, an image by default or anything projected in its place. The footer is a strip of text below
 * it, and on expand that strip grows into a full page carrying the projected content.
 *
 * The card in place is only ever measured, never moved. A copy is what animates, so this can sit
 * inside a carousel without disturbing its slide measurements.
 *
 * ```html
 * <fgbcl-expandable-card [image]="item.Image" [footerTitle]="item.Name" [caption]="item.Summary">
 *   <ng-template #headerContent>
 *     <fgbcl-video-player [videoId]="item.VideoId"></fgbcl-video-player>
 *   </ng-template>
 *   <ng-template #expandedContent>
 *     <div [innerHTML]="item.Description"></div>
 *   </ng-template>
 * </fgbcl-expandable-card>
 * ```
 */
@Component({
  selector: 'fgbcl-expandable-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './expandable-card.component.html',
  styleUrls: ['./expandable-card.component.scss'],
})
export class ExpandableCardComponent implements OnDestroy {
  /** Image source for the default header. Already resolved, this component does no URL building. */
  @Input() image: string;
  @Input() imageAlt: string = '';
  /** Set false to drop the header entirely, for a card that is text only. */
  @Input() showImage: boolean = true;

  /** First line of the footer strip. */
  @Input() footerTitle: string;
  /** Second line of the footer strip, trimmed with an ellipsis. */
  @Input() caption: string;
  /** Set false to show the title alone, without the caption beneath it. */
  @Input() showDescription: boolean = true;
  /** How many lines the caption gets before it ellipsises. */
  @Input() captionLines: number = 2;
  /**
   * Corner radius of the collapsed card, morphed to 0 as it fills the screen.
   *
   * Left unset it follows the theme's own border radius, published as a custom property by
   * gs-expandable-card. Pass a number to override it for a particular card.
   */
  @Input() radius: number | undefined;
  /** Set false to make the card inert, ie. when there is nothing more to show. */
  @Input() expandable: boolean = true;

  /** Anything projected here is shown in the footer once expanded. */
  @ContentChild('expandedContent') expandedContent: TemplateRef<unknown>;
  /** Replaces the default header, for cases like a video player instead of an image. */
  @ContentChild('headerContent') headerContent: TemplateRef<unknown>;

  @ViewChild('card') private _card: ElementRef<HTMLElement>;
  @ViewChild('overlay') private _overlay: ElementRef<HTMLElement>;
  @ViewChild('panel') private _panel: ElementRef<HTMLElement>;
  @ViewChild('inner') private _inner: ElementRef<HTMLElement>;
  @ViewChild('scrim') private _scrim: ElementRef<HTMLElement>;
  @ViewChild('article') private _article: ElementRef<HTMLElement>;
  @ViewChild('closeBtn') private _closeBtn: ElementRef<HTMLElement>;

  isExpanded: boolean = false;
  /** Expanded content is held back until the box has settled, matching the reference. */
  showArticle: boolean = false;
  /** The strip's own text collapses away as the card opens, it belongs to the collapsed state. */
  hideFooterText: boolean = false;
  /** Dropped from the DOM once collapsed, so nothing of the strip is left in the fullscreen view. */
  footerTextGone: boolean = false;
  /** The card's own strip, hidden behind the panel while open. */
  cardFooterHidden: boolean = false;
  /** Runs the fade in once the panel has gone and the card is back at carousel size. */
  cardFooterRevealing: boolean = false;
  /** Drives will-change, so the panel is only layer promoted while it is genuinely moving. */
  isAnimating: boolean = false;
  private _animating: boolean = false;
  private _timeline: gsap.core.Timeline | undefined;
  private _onViewportChange: (() => void) | undefined;

  constructor(
    private renderer: Renderer2,
    private cd: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) {}

  /**
   * Opens the card out to fullscreen.
   *
   * Width and height expand together on one curve, matching the reference. The footer growing into
   * a full page underneath is what makes it read as opening rather than a plain zoom, so the box
   * itself does not need staggering to sell it.
   */
  open(event?: Event): void {
    if (!this.expandable || this.isExpanded || this._animating) return;

    // Let links and buttons inside the card keep their own behaviour.
    const target = event?.target as HTMLElement | undefined;
    if (target?.closest('a, button:not(.expandable-card-close)')) return;

    const card = this._card?.nativeElement;
    if (!card) return;

    const from = card.getBoundingClientRect();
    // Measured off the card before the panel exists. Once the panel is rendered its own footer is
    // laid out at full width, which is taller than the strip on the card and would start the growth
    // most of the way to its destination.
    const stripHeight = this._cardFooterHeight();

    this._animating = true;
    this.isAnimating = true;
    this.isExpanded = true;
    this.showArticle = false;
    // Rendered with the strip still showing, so the collapse has a height to animate away from.
    this.hideFooterText = false;
    this.footerTextGone = false;
    // The card sits behind the panel the whole time it is open, so its strip is hidden outright and
    // faded back in only once the card is home.
    this.cardFooterHidden = true;
    this.cardFooterRevealing = false;
    this._lockScroll(true);
    this.cd.detectChanges();

    // Queried off the overlay rather than taken from ViewChild, which has not resolved yet in the
    // same tick the *ngIf first creates it. Reading it from ViewChild here yields undefined and the
    // open bails out before it starts.
    const panel =
      this._panel?.nativeElement ?? (this.host.nativeElement.querySelector('.expandable-card-panel') as HTMLElement | null);
    if (!panel) {
      this._animating = false;
      this.isExpanded = false;
      this._lockScroll(false);
      this.cd.detectChanges();
      return;
    }

    // Sized from the measured viewport before anything animates, and kept in step while open.
    this._applyViewportHeight(panel);
    this._watchViewport(panel);

    // A transformed ancestor becomes the containing block for position: fixed, and carousels
    // transform their track. Moving the overlay to body is what lets the panel reach the viewport.
    this._detachOverlay();

    const closeBtn = this._closeBtn?.nativeElement;
    const vw = window.innerWidth;
    const vh = this._viewportHeight();

    const inner = this._inner?.nativeElement;

    // The panel is laid out fullscreen and moved onto the card, then released. Only the width is
    // scaled, the height is driven by the white panel growing underneath a header that keeps its size.
    // Scaling height too would fight that growth and squash the header on the way out.
    const scaleX = from.width / vw;

    this._timeline?.kill();
    this._timeline = gsap.timeline({
      onComplete: () => {
        this._animating = false;
        // Dropped before the content renders, so the panel is off its own compositor layer by the
        // time anything is added to it.
        this.isAnimating = false;
        // Back to plain layout, otherwise the panel stays a containing block and its own scrolling
        // misbehaves, and the fixed heights would stop the content flowing.
        gsap.set(panel, { clearProps: 'transform,borderRadius,height,boxShadow' });
        if (inner) gsap.set(inner, { clearProps: 'transform' });
        const headerEl = panel.querySelector('.expandable-card-header');
        if (headerEl) gsap.set(headerEl, { clearProps: 'height' });
        if (this._article?.nativeElement) {
          gsap.set(this._article.nativeElement, { clearProps: 'height,minHeight,flexGrow' });
        }
        // Content arrives only once the box has settled, and the collapsed strip is dropped for
        // good rather than left as a zero height element in the page.
        this.footerTextGone = true;
        this.showArticle = true;
        this.cd.detectChanges();
        // iOS can still hold a stale layer here. Reading a layout property after the content is in
        // forces the repaint that otherwise waits for the first touch or scroll.
        void panel.offsetHeight;
      },
    });

    // The header keeps the height it has on the card and only grows once the panel is full width, so
    // the image is never stretched. The white panel below is what opens the page up.
    const header = panel.querySelector('.expandable-card-header');
    const article = this._article?.nativeElement;
    const headerFrom = Math.max(0, from.height - stripHeight);
    const headerTo = this._headerHeight();

    gsap.set(panel, {
      transformOrigin: '50% 0',
      x: from.left + from.width / 2 - vw / 2,
      y: from.top,
      scaleX,
      height: from.height,
      borderRadius: this.resolvedRadius / scaleX,
      boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
      force3D: true,
    });

    // Lifts as the card detaches, then flattens again once it is filling the screen.
    this._timeline.to(panel, { boxShadow: LIFT_SHADOW, duration: EXPAND_DURATION * 0.4, ease: 'power2.out' }, 0);
    this._timeline.to(
      panel,
      { boxShadow: '0 0 0 rgba(0, 0, 0, 0)', duration: EXPAND_DURATION * 0.45, ease: 'power2.in' },
      EXPAND_DURATION * 0.55
    );

    // Contents are counter-scaled horizontally only, so text is not squeezed while the panel is narrow.
    if (inner) {
      gsap.set(inner, { transformOrigin: '50% 0', scaleX: 1 / scaleX, force3D: true });
    }
    if (header) {
      gsap.set(header, { height: headerFrom });
    }
    if (article) {
      gsap.set(article, { height: stripHeight, minHeight: 0, flexGrow: 0 });
    }

    // Rises to the top of the screen while its height fills the viewport.
    this._timeline.to(panel, { y: 0, height: vh, duration: EXPAND_DURATION, ease: 'power3.out' }, 0);

    // Width on the same curve and duration as the height, both edges moving from the first frame.
    this._timeline.to(panel, { x: 0, scaleX: 1, duration: EXPAND_DURATION, ease: 'power3.out' }, 0);

    if (inner) {
      this._timeline.to(inner, { scaleX: 1, duration: EXPAND_DURATION, ease: 'power3.out' }, 0);
    }

    /**
     * The header rides the same measured curve. In the reference its bottom edge and the white area
     * below it move as one, so giving it a different curve makes the two fight and flattens out the
     * growth the white area is supposed to show.
     */
    if (header) {
      this._timeline.to(header, { height: headerTo, duration: ARTICLE_GROW_DURATION, ease: ARTICLE_GROW_EASE }, 0);
    }

    // The white area is the thing being watched, so it outlasts the box and keeps opening after the
    // rest has landed, which is what makes the growth legible rather than instant.
    if (article) {
      this._timeline.to(
        article,
        {
          height: Math.max(stripHeight, vh - headerTo),
          duration: ARTICLE_GROW_DURATION,
          ease: ARTICLE_GROW_EASE,
        },
        0
      );
    }

    // Collapsed on the next frame rather than now, a class set in the same frame as the initial
    // render gives the browser no starting height to transition from and it would simply snap.
    // A class rather than a tween, so the later render that brings in the body cannot restore it.
    requestAnimationFrame(() => {
      this.hideFooterText = true;
      this.cd.detectChanges();
    });

    // Radius is the last thing to go. The corners stay visibly round while the box travels and only
    // flatten over the back stretch, which is what keeps it reading as a card until it is a page.
    this._timeline.to(panel, { borderRadius: 0, duration: EXPAND_DURATION * 0.55, ease: 'power2.in' }, EXPAND_DURATION * 0.45);

    // Goes fully opaque early, so the feed is gone rather than showing through behind the card.
    const scrim = this._scrim?.nativeElement;
    if (scrim) {
      this._timeline.fromTo(scrim, { opacity: 0 }, { opacity: 1, duration: EXPAND_DURATION * 0.6, ease: 'power2.out' }, 0);
    }

    if (closeBtn) {
      // Appears early at full size rather than scaling up with the panel.
      this._timeline.fromTo(closeBtn, { opacity: 0 }, { opacity: 1, duration: 0.12, ease: 'power1.out' }, EXPAND_DURATION * 0.3);
    }
  }

  /** Shrinks the copy back onto the card, then removes it. */
  close(event?: Event): void {
    event?.stopPropagation();
    if (!this.isExpanded || this._animating) return;

    const card = this._card?.nativeElement;
    const panel = this._panel?.nativeElement;
    if (!card || !panel) {
      this._teardown();
      return;
    }

    const to = card.getBoundingClientRect();
    const article = this._article?.nativeElement;
    const closeBtn = this._closeBtn?.nativeElement;
    const vw = window.innerWidth;
    const vh = this._viewportHeight();

    this._animating = true;
    this.isAnimating = true;
    this._timeline?.kill();
    this._timeline = gsap.timeline({
      onComplete: () => {
        this._animating = false;
        this.isAnimating = false;
        this._teardown();
        // The overlay is gone now, so the card's own strip fades in at its carousel size.
        this.cardFooterHidden = false;
        this.cardFooterRevealing = true;
        this.cd.detectChanges();
        // Cleared once it has played, so a later open starts from a clean state.
        setTimeout(() => {
          this.cardFooterRevealing = false;
          this.cd.detectChanges();
        }, 320);
      },
    });

    const inner = this._inner?.nativeElement;
    const scaleX = to.width / vw;
    const header = panel.querySelector('.expandable-card-header');
    const stripHeight = this._cardFooterHeight();
    const headerTo = Math.max(0, to.height - stripHeight);

    // The panel's strip stays out for the whole close. Bringing it back mid shrink would show the
    // title twice over, once here and again on the card underneath as it is revealed.

    // Everything on the page fades out first and the box only starts moving once it has gone, so
    // nothing is being relaid out while the panel shrinks.
    const body = panel.querySelector('.expandable-card-body');
    if (body instanceof HTMLElement) {
      // Frozen at its current height and taken out of flow sizing, so the auto margin holding the
      // action down cannot recompute while the article shrinks underneath it. It stays fully opaque
      // through the shrink and only fades over the last stretch.
      gsap.set(body, { height: body.offsetHeight, flexGrow: 0, overflow: 'hidden' });
      this._timeline.to(body, { opacity: 0, duration: COLLAPSE_FADE, ease: 'power2.in' }, COLLAPSE_DURATION - COLLAPSE_FADE);
    }
    // The close button goes immediately, it is chrome rather than content.
    if (closeBtn) {
      this._timeline.to(closeBtn, { opacity: 0, duration: 0.1, ease: 'power2.in' }, 0);
    }

    // clearProps ran on open, so restate the fullscreen box before shrinking it back.
    gsap.set(panel, {
      transformOrigin: '50% 0',
      x: 0,
      y: 0,
      scaleX: 1,
      height: vh,
      borderRadius: 0,
      boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
      force3D: true,
    });

    // Lifts again while it shrinks, then settles flat back into the carousel.
    this._timeline.to(panel, { boxShadow: LIFT_SHADOW, duration: COLLAPSE_DURATION * 0.4, ease: 'power2.out' }, 0);
    this._timeline.to(
      panel,
      { boxShadow: '0 0 0 rgba(0, 0, 0, 0)', duration: COLLAPSE_DURATION * 0.4, ease: 'power2.in' },
      COLLAPSE_DURATION * 0.6
    );
    if (inner) gsap.set(inner, { transformOrigin: '50% 0', scaleX: 1, force3D: true });
    if (header) gsap.set(header, { height: this._headerHeight() });
    if (article) gsap.set(article, { flexGrow: 0, minHeight: 0 });

    this._timeline.to(
      panel,
      {
        x: to.left + to.width / 2 - vw / 2,
        scaleX,
        duration: COLLAPSE_DURATION,
        ease: 'power3.in',
      },
      0
    );

    // Corners round back early on the way in, the mirror of flattening last on the way out.
    this._timeline.to(
      panel,
      { borderRadius: this.resolvedRadius / scaleX, duration: COLLAPSE_DURATION * 0.55, ease: 'power2.out' },
      0
    );

    this._timeline.to(panel, { y: to.top, height: to.height, duration: COLLAPSE_DURATION, ease: 'power3.in' }, 0);

    if (inner) {
      this._timeline.to(inner, { scaleX: 1 / scaleX, duration: COLLAPSE_DURATION, ease: 'power3.in' }, 0);
    }
    if (header) {
      this._timeline.to(header, { height: headerTo, duration: COLLAPSE_DURATION, ease: 'power3.in' }, 0);
    }
    if (article) {
      this._timeline.to(article, { height: stripHeight, duration: COLLAPSE_DURATION * 0.85, ease: 'power2.in' }, 0);
    }

    const scrim = this._scrim?.nativeElement;
    if (scrim) {
      this._timeline.to(scrim, { opacity: 0, duration: COLLAPSE_DURATION * 0.8, ease: 'power2.in' }, 0);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isExpanded) this.close();
  }

  /**
   * Height of the footer strip as it sits on the collapsed card.
   *
   * Read from the card, never the panel. The panel's own footer is laid out at full viewport width,
   * so it is shorter in line count and taller in padding terms than the strip in a narrow carousel
   * slide, and starting from it would skip most of the growth.
   *
   * offsetHeight rather than a client rect, because the panel is mid-transform when these are read
   * and a rect would report the scaled size rather than the laid out one.
   */
  private _cardFooterHeight(): number {
    const footer = this._card?.nativeElement?.querySelector('.expandable-card-footer');
    return footer instanceof HTMLElement ? footer.offsetHeight : 0;
  }

  /** Height the header occupies in the expanded panel, so the white area can take the remainder. */
  private _headerHeight(): number {
    const header = this._panel?.nativeElement?.querySelector('.expandable-card-header');
    return header instanceof HTMLElement ? header.offsetHeight : 0;
  }

  /**
   * Writes the measured viewport height onto the panel, so its height is a real pixel value rather
   * than a unit the browser resolves against the wrong viewport.
   */
  private _applyViewportHeight(panel: HTMLElement): void {
    panel.style.setProperty('--expandable-card-viewport-height', `${this._viewportHeight()}px`);
  }

  /**
   * Keeps that value current. iOS reports a different height as the floating toolbar slides in and
   * out, and without this the panel keeps whatever it measured when it opened.
   */
  private _watchViewport(panel: HTMLElement): void {
    this._stopWatchingViewport();

    this._onViewportChange = () => {
      // A card opened on mobile has no place on desktop, where the caller may not even render it,
      // so crossing the breakpoint dismisses it. Not close(), that animates back onto a card which
      // by then has moved or gone, which is the stretched, misplaced frame you see mid resize.
      if (this._isDesktopWidth()) {
        this._dismiss();
        return;
      }

      this._applyViewportHeight(panel);
    };
    window.visualViewport?.addEventListener('resize', this._onViewportChange);
    window.visualViewport?.addEventListener('scroll', this._onViewportChange);
    window.addEventListener('resize', this._onViewportChange);
  }

  private _stopWatchingViewport(): void {
    if (!this._onViewportChange) return;

    window.visualViewport?.removeEventListener('resize', this._onViewportChange);
    window.visualViewport?.removeEventListener('scroll', this._onViewportChange);
    window.removeEventListener('resize', this._onViewportChange);
    this._onViewportChange = undefined;
  }

  /**
   * The radius actually in play, the input when one is given and the themed value otherwise.
   *
   * Read back off the host rather than hardcoded, so a club overriding $border-radius changes the
   * card and the animation together instead of only the static corners.
   */
  get resolvedRadius(): number {
    if (typeof this.radius === 'number') return this.radius;

    const raw = window.getComputedStyle(this.host.nativeElement).getPropertyValue('--expandable-card-radius');
    const parsed = parseFloat(raw);

    return Number.isFinite(parsed) ? parsed : 16;
  }

  /** Drops the overlay with no animation, for when there is nothing sensible to animate back to. */
  private _dismiss(): void {
    this._timeline?.kill();
    this._timeline = undefined;
    this._animating = false;
    this.isAnimating = false;
    this._teardown();
    this.cardFooterHidden = false;
    this.cardFooterRevealing = false;
    this.cd.detectChanges();
  }

  /**
   * Whether the window is at or past the desktop breakpoint.
   *
   * Read from the same bootstrap custom property the ifScreenSize directive uses, so the card closes
   * at exactly the width the caller swaps its layout at rather than at a number of our own.
   */
  private _isDesktopWidth(): boolean {
    const body = this.host.nativeElement.ownerDocument?.body;
    if (!body) return false;

    const raw = window.getComputedStyle(body).getPropertyValue('--bs-breakpoint-lg');
    const breakpoint = parseFloat(raw);
    if (!Number.isFinite(breakpoint)) return false;

    return window.innerWidth >= breakpoint;
  }

  /**
   * Visible viewport height, matching the dvh the stylesheet sizes the panel with.
   *
   * visualViewport is what iOS updates as the browser chrome slides away, where innerHeight reports
   * the larger figure and would animate the panel to a height taller than the screen.
   */
  private _viewportHeight(): number {
    // No inset subtraction, visualViewport already reports the area the chrome leaves. The panel
    // fills that, and whatever is anchored to its bottom applies the inset itself.
    return window.visualViewport?.height ?? window.innerHeight;
  }

  /**
   * The overlay element, whether it is still in this view or already moved out to body.
   *
   * Falls back to a host query, since the ViewChild has not resolved in the same tick the *ngIf
   * first creates it.
   */
  private _overlayEl(): HTMLElement | null {
    return (
      this._overlay?.nativeElement ?? (this.host.nativeElement.querySelector('.expandable-card-overlay') as HTMLElement | null)
    );
  }

  /**
   * Moves the overlay out to body, so a transformed ancestor cannot trap the fixed panel or clip it
   * behind an overflow: hidden track.
   */
  private _detachOverlay(): void {
    const overlay = this._overlayEl();
    const body = this.host.nativeElement.ownerDocument?.body;
    if (!overlay || !body || overlay.parentElement === body) return;

    this.renderer.appendChild(body, overlay);
  }

  /** Takes the overlay off body, since Angular cannot destroy a node it no longer owns. */
  private _reattachOverlay(): void {
    const overlay = this._overlayEl();
    if (!overlay || !overlay.parentElement) return;

    this.renderer.removeChild(overlay.parentElement, overlay);
  }

  private _teardown(): void {
    this._stopWatchingViewport();
    this._timeline?.kill();
    this._timeline = undefined;
    this._reattachOverlay();
    this.isExpanded = false;
    this.showArticle = false;
    this.hideFooterText = false;
    this.footerTextGone = false;
    this._lockScroll(false);
    this.cd.detectChanges();
  }

  private _lockScroll(lock: boolean): void {
    const body = this.host.nativeElement.ownerDocument?.body;
    if (!body) return;

    if (lock) {
      this.renderer.addClass(body, 'expandable-card-open');
    } else {
      this.renderer.removeClass(body, 'expandable-card-open');
    }
  }

  ngOnDestroy(): void {
    this._stopWatchingViewport();
    this._timeline?.kill();
    this.cardFooterHidden = false;
    this._reattachOverlay();
    this._lockScroll(false);
  }
}
